import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Grid, Typography, Divider, Stack, CircularProgress,
} from '@mui/material';
import dayjs    from 'dayjs';
import axios    from 'axios';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import { useGrowSetup }   from '../../GrowSetupContext';
import { AuthContext }    from '../../components/signin/AuthContext';
import CombinedGraph      from '../../components/CombinedGraph';
import CustomDatePicker   from '../../components/CustomDatePicker';
import AnalyticsChart     from '../components/AnalyticsChart';
import { useV4Resolve }   from '../theme';

/* ─── Actor / sensor definitions ─────────────────────── */
const ACTORS = [
  { key: 'licht',   label: 'Licht',   icon: '☀️', color: 'var(--v4-gold)' },
  { key: 'abluft',  label: 'Abluft',  icon: '💨', color: 'var(--v4-teal)' },
  { key: 'umluft',  label: 'Umluft',  icon: '🌀', color: '#a88fff' },
  { key: 'heizung', label: 'Heizung', icon: '🔥', color: '#ff5555' },
];
const DEVICES = [
  { key: 'bme',            label: 'Sensor BME',        icon: '📡', color: '#a0a0ff' },
  { key: 'sht',            label: 'Sensor SHT',        icon: '📟', color: '#ffa0a0' },
  { key: 'klimakontroller', label: 'Klimakontroller',   icon: '🌡️', color: 'var(--v4-cyan)' },
  { key: 'hydrokontroller', label: 'Hydro Kontroller',  icon: '💧', color: 'var(--v4-blue)' },
];

/* ─── Section label ──────────────────────────────────── */
function SectionLabel({ children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, mt: 3 }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(var(--v4-cyan-rgb),0.6)', whiteSpace: 'nowrap' }}>
        {children}
      </Typography>
      <Divider sx={{ flex: 1, borderColor: 'rgba(var(--v4-cyan-rgb),0.08)' }} />
    </Stack>
  );
}

/* ─── Area gradient helper ───────────────────────────── */
function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%"   stopColor={color} stopOpacity={0.35} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

/* ─── Actor / Device status card ─────────────────────── */
function StatusCard({ cardDef, gaugeVal, grow_setup, animDelay }) {
  const { token }   = useContext(AuthContext);
  const { icon, label, key } = cardDef;
  const color = useV4Resolve()(cardDef.color);
  const [chart,   setChart]   = useState([]);
  const [loading, setLoading] = useState(true);
  const gradId = `analytics-grad-${key}`;

  const isOn = gaugeVal != null && Number(gaugeVal) > 0;

  useEffect(() => {
    if (!grow_setup) return;
    const end = new Date(), start = new Date(end - 24 * 3600 * 1000);
    axios.post('/get_measures_by_timedelta/', {
      start_time: start, end_time: end,
      grow_setup_id: grow_setup, aggregation_level: '5m', measure: key,
    }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } })
      .then(res => {
        const row = res?.data?.[0];
        setChart(row?.data ?? []);
      }).catch(console.error)
      .finally(() => setLoading(false));
  }, [grow_setup, key]); // eslint-disable-line react-hooks/exhaustive-deps

  /* compute activity % from sparkline */
  const actPct = chart.length > 0
    ? Math.round((chart.filter(v => v > 0).length / chart.length) * 100)
    : null;

  return (
    <Box sx={{
      background: 'rgba(var(--v4-surface-rgb),0.9)',
      border: `1px solid ${isOn ? `color-mix(in srgb, ${color} 33%, transparent)` : `color-mix(in srgb, ${color} 9%, transparent)`}`,
      borderRadius: '18px',
      backdropFilter: 'blur(20px)',
      overflow: 'hidden',
      transition: 'border-color 0.3s, box-shadow 0.3s',
      boxShadow: isOn ? `0 4px 28px color-mix(in srgb, ${color} 9%, transparent)` : 'none',
      animation: `v4FadeUp 0.55s cubic-bezier(0.22,1,0.36,1) ${animDelay}ms both`,
    }}>
      {/* Header row */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '11px',
          background: `color-mix(in srgb, ${color} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 27%, transparent)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: 'var(--v4-text)', fontSize: '0.88rem', lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.3 }}>
            <Box sx={{
              width: 6, height: 6, borderRadius: '50%',
              background: isOn ? 'var(--v4-green)' : 'rgba(var(--v4-fg-rgb),0.2)',
              boxShadow: isOn ? '0 0 6px var(--v4-green)' : 'none',
              flexShrink: 0,
              animation: isOn ? 'v4Pulse 2s infinite' : 'none',
            }} />
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: isOn ? 'var(--v4-green)' : 'rgba(var(--v4-fg-rgb),0.25)', letterSpacing: '0.08em' }}>
              {isOn ? 'AN' : 'AUS'}
            </Typography>
          </Stack>
        </Box>
        {/* Activity percentage badge */}
        {actPct !== null && (
          <Box sx={{
            px: 1.25, py: 0.4, borderRadius: '20px',
            background: `color-mix(in srgb, ${color} 8%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`,
          }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 800, color }}>
              {actPct}%
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Sparkline */}
      <Box sx={{ px: 1.5, pb: 2, height: 52 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress size={16} sx={{ color: `color-mix(in srgb, ${color} 40%, transparent)` }} />
          </Box>
        ) : chart.length > 0 ? (
          <SparkLineChart
            data={chart}
            colors={[color]}
            area showHighlight showTooltip
            sx={{
              [`& .${areaElementClasses.root}`]: { fill: `url(#${gradId})` },
              '& .MuiChartsAxis-root': { display: 'none' },
            }}
          >
            <AreaGradient color={color} id={gradId} />
          </SparkLineChart>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(var(--v4-muted-rgb),0.2)' }}>Keine Daten</Typography>
          </Box>
        )}
      </Box>

      {/* Activity bar */}
      {actPct !== null && (
        <Box sx={{ mx: 2.5, mb: 2, height: 3, borderRadius: 2, background: 'rgba(var(--v4-fg-rgb),0.05)', overflow: 'hidden' }}>
          <Box sx={{
            height: '100%', width: `${actPct}%`, borderRadius: 2,
            background: `linear-gradient(90deg, color-mix(in srgb, ${color} 53%, transparent), ${color})`,
            transition: 'width 1s ease',
            boxShadow: `0 0 6px ${color}`,
          }} />
        </Box>
      )}
    </Box>
  );
}

/* ─── Summary stat tile ──────────────────────────────── */
function StatTile({ label, value, color: colorProp, icon }) {
  const color = useV4Resolve()(colorProp);
  return (
    <Box sx={{
      flex: '1 1 140px', minWidth: 120,
      background: 'rgba(var(--v4-surface-rgb),0.9)',
      border: `1px solid color-mix(in srgb, ${color} 13%, transparent)`,
      borderRadius: '16px', px: 2.5, py: 1.75,
      boxShadow: `0 4px 20px color-mix(in srgb, ${color} 5%, transparent)`,
      animation: 'v4FadeUp 0.4s both',
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontSize: 18 }}>{icon}</Typography>
        <Box>
          <Typography sx={{ fontSize: '0.58rem', color: 'rgba(var(--v4-muted-rgb),0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, color, fontSize: '1.05rem', fontFamily: 'monospace' }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/* ─── Page ───────────────────────────────────────────── */
export default function V4AnalyticsPage() {
  const { grow_setup } = useGrowSetup();
  const { token }      = useContext(AuthContext);
  const [gauges,   setGauges] = useState({});
  const [timeFrom, setFrom]   = useState(dayjs().subtract(1, 'day'));
  const [timeTo,   setTo]     = useState(dayjs());

  useEffect(() => {
    if (!grow_setup) return;
    const load = () =>
      axios.post(`/get_current_gauge_values/${grow_setup}`, { hello: 'world' }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }).then(r => setGauges(r.data || {})).catch(console.error);
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [grow_setup]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeActors  = ACTORS.filter(a => Number(gauges[a.key]) > 0).length;
  const activeDevices = DEVICES.filter(d => Number(gauges[d.key]) > 0).length;
  const rangeHours    = timeTo.diff(timeFrom, 'hour');

  return (
    <Box>
      <style>{`
        @keyframes v4FadeUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes v4Pulse  { 0%,100%{box-shadow:0 0 0 0 color-mix(in srgb, var(--v4-green) 53%, transparent)} 50%{box-shadow:0 0 0 6px color-mix(in srgb, var(--v4-green) 0%, transparent)} }
      `}</style>

      {/* ── Summary tiles ── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <StatTile label="Zeitraum"         value={`${rangeHours}h`}    color="var(--v4-cyan)" icon="⏱️" />
        <StatTile label="Aktoren aktiv"    value={`${activeActors}/4`} color="var(--v4-green)" icon="⚡" />
        <StatTile label="Geräte aktiv"     value={`${activeDevices}/4`} color="var(--v4-purple)" icon="📡" />
      </Box>

      {/* ── Date range picker ── */}
      <Box sx={{
        display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3,
        p: 2,
        background: 'rgba(var(--v4-surface-rgb),0.9)',
        border: '1px solid rgba(var(--v4-cyan-rgb),0.1)',
        borderRadius: '16px',
        backdropFilter: 'blur(20px)',
        animation: 'v4FadeUp 0.4s both',
        '& .MuiInputBase-root': {
          background: 'rgba(var(--v4-cyan-rgb),0.04) !important',
          borderRadius: '10px !important',
          '& fieldset': { borderColor: 'rgba(var(--v4-cyan-rgb),0.15) !important' },
          '&:hover fieldset': { borderColor: 'rgba(var(--v4-cyan-rgb),0.3) !important' },
          '&.Mui-focused fieldset': { borderColor: 'var(--v4-cyan) !important' },
        },
        '& .MuiTypography-root': { color: 'rgba(var(--v4-muted-rgb),0.7)' },
        '& .MuiSvgIcon-root': { color: 'rgba(var(--v4-cyan-rgb),0.5)' },
        '& .MuiInputLabel-root': { color: 'rgba(var(--v4-muted-rgb),0.5) !important' },
      }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 1 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(var(--v4-cyan-rgb),0.5)' }}>
            Zeitraum
          </Typography>
        </Stack>
        <CustomDatePicker label="Startzeit" value={timeFrom} setValueOuter={setFrom} />
        <CustomDatePicker label="Endzeit"   value={timeTo}   setValueOuter={setTo}   />
      </Box>

      {/* ── New Chart ── */}
      <Box sx={{ mb: 4, animation: 'v4FadeUp 0.5s 60ms both' }}>
        <AnalyticsChart grow_setup={grow_setup} time_from={timeFrom} time_to={timeTo} />
      </Box>

      {/* ── Legacy Chart (Fallback) ── */}
      <Box sx={{
        background: 'rgba(var(--v4-surface-rgb),0.9)',
        border: '1px solid rgba(var(--v4-cyan-rgb),0.06)',
        borderRadius: '16px',
        backdropFilter: 'blur(20px)',
        overflow: 'hidden', mb: 4,
        animation: 'v4FadeUp 0.5s 80ms both',
        opacity: 0.7,
        '& .MuiCard-root,& .MuiPaper-root': { background: 'transparent !important', border: 'none !important', boxShadow: 'none !important' },
        '& .MuiTypography-root': { color: 'rgba(var(--v4-muted-rgb),0.8)' },
        '& .MuiChartsAxis-tickLabel': { fill: 'rgba(var(--v4-muted-rgb),0.5) !important' },
        '& .MuiChartsAxis-line,& .MuiChartsAxis-tick': { stroke: 'rgba(var(--v4-cyan-rgb),0.12) !important' },
        '& .MuiChartsGrid-line': { stroke: 'rgba(var(--v4-cyan-rgb),0.06) !important' },
      }}>
        <CombinedGraph grow_setup={grow_setup} time_from={timeFrom} time_to={timeTo} />
      </Box>

      {/* ── Aktoren Status ── */}
      <SectionLabel>Aktoren — Live Status</SectionLabel>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {ACTORS.map((a, i) => (
          <Grid item xs={12} sm={6} md={3} key={a.key}>
            <StatusCard
              cardDef={a}
              gaugeVal={gauges[a.key]}
              grow_setup={grow_setup}
              animDelay={i * 60}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Geräte / Controller Status ── */}
      <SectionLabel>Geräte & Kontroller — Live Status</SectionLabel>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {DEVICES.map((d, i) => (
          <Grid item xs={12} sm={6} md={3} key={d.key}>
            <StatusCard
              cardDef={d}
              gaugeVal={gauges[d.key]}
              grow_setup={grow_setup}
              animDelay={i * 60}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
